/**
 * Screen-2 demo products (OEM | Aftermarket | Used).
 * Built from the selected vehicle so BMW never shows Maruti Swift cards.
 */

const PART_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
      <rect width="120" height="120" rx="12" fill="#0f172a"/>
      <rect x="18" y="28" width="84" height="64" rx="8" fill="#1e293b" stroke="#334155"/>
      <text x="60" y="66" text-anchor="middle" fill="#94a3b8" font-size="11" font-family="Arial,sans-serif">Part</text>
    </svg>`
  );

function slugToken(value) {
  return String(value || "part")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "")
    .slice(0, 8) || "PART";
}

function oemCode(make, model, year, n) {
  const a = slugToken(make).slice(0, 3);
  const b = slugToken(model).slice(0, 4);
  const y = String(year || "2020").slice(-2);
  return `${a}${b}-${y}${n}0-G00`;
}

/**
 * @param {{ make?: string, model?: string, year?: string|number }} vehicle
 */
export function buildCatalogCompareDummyProducts(vehicle = {}) {
  let make = String(vehicle.make || "").trim();
  let model = String(vehicle.model || "").trim();
  const year = String(vehicle.year || "").trim() || "2022";

  // Avoid "BMW BMW i5" when model already includes make
  if (make && model.toLowerCase().startsWith(make.toLowerCase() + " ")) {
    model = model.slice(make.length).trim();
  }

  if (!make && !model) {
    make = "Maruti";
    model = "Swift";
  } else if (!make) {
    make = "Vehicle";
  } else if (!model) {
    model = "Model";
  }

  const vehicleLabel = `${make} ${model}`.trim();
  const yearSpan = year ? `${Math.max(2017, Number(year) - 5)}–${year}` : "2017–2023";
  const token = slugToken(`${make}${model}`);
  const oemBrand = `${make} Genuine`;

  return [
    // —— OEM ——
    {
      id: `demo-${token}-oem-1`,
      name: `Genuine Front Brake Pad Set — ${vehicleLabel} ${yearSpan}`,
      brand: oemBrand,
      partNumber: oemCode(make, model, year, 1),
      sku: `${token}-OEM-BP-01`,
      price: make.toLowerCase().includes("bmw") ? 6840 : 1560,
      mrp: make.toLowerCase().includes("bmw") ? 8200 : 1800,
      discount: 17,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: true,
      origin: "OEM",
      condition: "new",
      stock: 12,
      rating: 4.3,
      warrantyMonths: 12,
      soldBy: `${make} Authorized`,
      deliveryDays: 1,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-oem-2`,
      name: `Bosch OEM Front Brake Pad Set — ${vehicleLabel}`,
      brand: "Bosch OEM",
      partNumber: oemCode(make, model, year, 2),
      sku: `BSCH-${token}-BP`,
      price: make.toLowerCase().includes("bmw") ? 5420 : 1240,
      mrp: make.toLowerCase().includes("bmw") ? 6900 : 1680,
      discount: 21,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: true,
      origin: "OEM",
      condition: "new",
      stock: 28,
      rating: 4.2,
      warrantyMonths: 12,
      soldBy: "AutoZone Delhi",
      deliveryDays: 1,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-oem-3`,
      name: `Akebono Ultra-Premium Ceramic OEM Pads — ${vehicleLabel}`,
      brand: "Akebono OEM",
      partNumber: oemCode(make, model, year, 3),
      sku: `AKE-${token}-CE`,
      price: make.toLowerCase().includes("bmw") ? 9120 : 1880,
      mrp: make.toLowerCase().includes("bmw") ? 10500 : 2200,
      discount: 13,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: true,
      origin: "OEM",
      condition: "new",
      stock: 8,
      rating: 4.6,
      warrantyMonths: 24,
      soldBy: "Sparepé Direct",
      deliveryDays: 2,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    // —— Aftermarket ——
    {
      id: `demo-${token}-am-1`,
      name: `TVS Nano Ceramic Brake Pad Set — ${vehicleLabel}`,
      brand: "TVS Eurogrip",
      partNumber: oemCode(make, model, year, 4),
      sku: `TVS-${token}-NPD`,
      price: make.toLowerCase().includes("bmw") ? 3280 : 980,
      mrp: make.toLowerCase().includes("bmw") ? 4100 : 1200,
      discount: 20,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "new",
      stock: 40,
      rating: 4.7,
      warrantyMonths: 18,
      soldBy: "Sparepé Direct",
      deliveryDays: 1,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-am-2`,
      name: `Brembo Premium Disc Brake Pad — ${vehicleLabel}`,
      brand: "Brembo",
      partNumber: oemCode(make, model, year, 5),
      sku: `BRE-${token}-P06`,
      price: make.toLowerCase().includes("bmw") ? 11400 : 3640,
      mrp: make.toLowerCase().includes("bmw") ? 13200 : 4200,
      discount: 14,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "new",
      stock: 6,
      rating: 4.8,
      warrantyMonths: 24,
      soldBy: "Brembo India Auth.",
      deliveryDays: 2,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-am-3`,
      name: `Fras-le Semi-Metallic Brake Pads — ${vehicleLabel}`,
      brand: "Fras-le",
      partNumber: oemCode(make, model, year, 6),
      sku: `FRL-${token}-PD`,
      price: make.toLowerCase().includes("bmw") ? 2460 : 720,
      mrp: make.toLowerCase().includes("bmw") ? 3100 : 900,
      discount: 21,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "new",
      stock: 22,
      rating: 4.1,
      warrantyMonths: 12,
      soldBy: "PitStop Spares",
      deliveryDays: 1,
      fulfilledBySparelo: true,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    // —— Used ——
    {
      id: `demo-${token}-used-1`,
      name: `Front Brake Pad Set — ${vehicleLabel} ${year}, low km`,
      brand: `${make} Genuine (Pulled)`,
      partNumber: oemCode(make, model, year, 1),
      sku: `USED-${token}-001`,
      price: make.toLowerCase().includes("bmw") ? 1890 : 380,
      mrp: make.toLowerCase().includes("bmw") ? 6840 : 1560,
      discount: 72,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "used",
      usedGrade: "B",
      usedConditionRaw: "good",
      stock: 2,
      rating: 4.4,
      warrantyMonths: null,
      soldBy: `${make} Salvage Yard`,
      deliveryDays: 3,
      fulfilledBySparelo: false,
      freeDelivery: false,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-used-2`,
      name: `Bosch Ceramic Front Pads — ${vehicleLabel}, very low wear`,
      brand: "Bosch (Pulled OEM)",
      partNumber: oemCode(make, model, year, 2),
      sku: `USED-${token}-002`,
      price: make.toLowerCase().includes("bmw") ? 2450 : 480,
      mrp: make.toLowerCase().includes("bmw") ? 5420 : 1240,
      discount: 55,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "used",
      usedGrade: "A",
      usedConditionRaw: "excellent",
      stock: 1,
      rating: 4.6,
      warrantyMonths: null,
      soldBy: "Delhi Auto Salvage",
      deliveryDays: 2,
      fulfilledBySparelo: false,
      freeDelivery: true,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
    {
      id: `demo-${token}-used-3`,
      name: `TVS Nano Ceramic Pads — ${vehicleLabel}, inspected`,
      brand: "TVS Eurogrip (Pulled)",
      partNumber: oemCode(make, model, year, 4),
      sku: `USED-${token}-003`,
      price: make.toLowerCase().includes("bmw") ? 980 : 220,
      mrp: make.toLowerCase().includes("bmw") ? 3280 : 980,
      discount: 70,
      image: PART_IMG,
      images: [PART_IMG],
      isOEM: false,
      origin: "Aftermarket",
      condition: "used",
      usedGrade: "C",
      usedConditionRaw: "fair",
      stock: 3,
      rating: 4.1,
      warrantyMonths: null,
      soldBy: "Rajasthan Spares",
      deliveryDays: 4,
      fulfilledBySparelo: false,
      freeDelivery: false,
      category: "Brakes",
      vehicleCompatibility: [{ make, model, year }],
    },
  ];
}

/** @deprecated Prefer buildCatalogCompareDummyProducts(vehicle) */
export const CATALOG_COMPARE_DUMMY_PRODUCTS = buildCatalogCompareDummyProducts({
  make: "Maruti",
  model: "Swift",
  year: "2022",
});

export default buildCatalogCompareDummyProducts;
